import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaAngleLeft,
  FaAngleRight,
} from 'react-icons/fa';
import PageSizeSelector from '../../DataTable/PageSizeSelector';

interface PaginationProps {
  url: string;
  paginator: {
    number: number;
    totalPages: number;
    totalElements: number;
    first: boolean;
    last: boolean;
  };
  data: any[];
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void; // ✅ nueva prop para control externo
}

const AdvancedTablePagination: React.FC<PaginationProps> = ({
  paginator,
  pageSize,
  onPageSizeChange,
  onPageChange, // ✅ la recibimos
  data,
}) => {
  const currentPage = paginator?.number;
  const totalPages = paginator?.totalPages || 0;
  const first = paginator?.first || false;
  const last = paginator?.last || false;

  const visiblePageCount = 5;
  const startPage = Math.max(0, currentPage - Math.floor(visiblePageCount / 2));
  const endPage = Math.min(startPage + visiblePageCount - 1, totalPages - 1);

  // ✅ Nueva función desacoplada del router
  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages) {
      onPageChange(page); // usamos función externa
    }
  };

  return (
    <div className="flex flex-col gap-2 border-stroke px-4 pb-6 pt-4 dark:border-strokedark md:flex-row md:items-center md:justify-between">
      {/* Información de registros */}
      <p className="text-gray-700 text-center text-sm font-medium">
        Mostrando {currentPage + 1 || 1} de {totalPages} páginas
      </p>

      {/* Controles de paginación */}
      <nav className="flex justify-center">
        <ul className="flex items-center">
          {/* Primera Página */}
          <li>
            <button
              onClick={() => handlePageChange(0)}
              disabled={first}
              className={`flex h-9 w-9 items-center justify-center rounded-l-md border border-stroke ${
                first
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'hover:border-primary hover:bg-gray hover:text-primary dark:hover:border-primary dark:hover:bg-graydark'
              }`}
            >
              <FaAngleDoubleLeft />
            </button>
          </li>

          {/* Anterior */}
          <li>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={first}
              className={`flex h-9 w-9 items-center justify-center border border-stroke ${
                first
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'hover:border-primary hover:bg-gray hover:text-primary dark:hover:border-primary dark:hover:bg-graydark'
              }`}
            >
              <FaAngleLeft />
            </button>
          </li>

          {/* Números */}
          {Array.from({ length: endPage - startPage + 1 }, (_, index) => (
            <li key={startPage + index}>
              <button
                onClick={() => handlePageChange(startPage + index)}
                className={`flex items-center justify-center border border-stroke px-4 py-[5px] font-medium ${
                  currentPage === startPage + index
                    ? 'bg-primary text-white'
                    : 'hover:border-primary hover:bg-gray hover:text-primary dark:hover:border-primary dark:hover:bg-graydark'
                }`}
              >
                {startPage + index + 1}
              </button>
            </li>
          ))}

          {/* Siguiente */}
          <li>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={last}
              className={`flex h-9 w-9 items-center justify-center border border-stroke ${
                last
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'hover:border-primary hover:bg-gray hover:text-primary dark:hover:border-primary dark:hover:bg-graydark'
              }`}
            >
              <FaAngleRight />
            </button>
          </li>

          {/* Última */}
          <li>
            <button
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={last}
              className={`flex h-9 w-9 items-center justify-center rounded-r-md border border-stroke ${
                last
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'hover:border-primary hover:bg-gray hover:text-primary dark:hover:border-primary dark:hover:bg-graydark'
              }`}
            >
              <FaAngleDoubleRight />
            </button>
          </li>
        </ul>
      </nav>

      {/* Selector de tamaño */}
      <PageSizeSelector
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        disabled={!data || data.length === 0}
      />
    </div>
  );
};

export default AdvancedTablePagination;
